import useLanguageNavigate from '@hooks/useLanguageNavigate';
import BookIcon from '@mui/icons-material/Book';
import ChatIcon from '@mui/icons-material/Chat';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CloseIcon from '@mui/icons-material/Close';
import { Avatar, Button, Typography, Badge, Box, Modal, keyframes } from '@mui/material';
import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { acceptOffer, markCommentAsRead } from '../services/services';
import { Offer } from '../types';
import 'react-quill/dist/quill.snow.css';

const flagCDN = 'https://flagcdn.com/w160/';
export const ACCEPTED_STATUS = 'Accepted';

// Define the pulse animation
const pulseAnimation = keyframes`
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.7);
  }
  
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 5px rgba(244, 67, 54, 0);
  }
  
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
  }
`;

// Comment Modal Component
const CommentModal = ({ open, handleClose, comment }: { open: boolean; handleClose: () => void; comment: string }) => {
	const { t } = useTranslation();
	
	return (
		<Modal
			open={open}
			onClose={handleClose}
			aria-labelledby="comment-modal-title"
			aria-describedby="comment-modal-description"
			disableAutoFocus={true}
		>
			<Box sx={{
				position: 'absolute',
				top: '50%',
				left: '50%',
				transform: 'translate(-50%, -50%)',
				width: { xs: '90%', sm: 500 },
				bgcolor: 'background.paper',
				boxShadow: 24,
				p: 4,
				borderRadius: 2,
				maxHeight: '80vh',
				overflow: 'auto'
			}}>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
					<Typography id="comment-modal-title" variant="h6" component="h2">
						{t('comment')}
					</Typography>
					<Button onClick={handleClose} sx={{ color: 'gray' }}>
						<CloseIcon />
					</Button>
				</Box>
				<Box 
					sx={{
						'& ul': {
							paddingLeft: '15px',
							listStyleType: 'disc',
							margin: '4px 0',
						},
						'& ol': {
							paddingLeft: '15px',
							listStyleType: 'decimal',
							margin: '4px 0',
						},
						'& li': {
							display: 'list-item',
							margin: '2px 0',
						},
						'& a': {
							color: '#3da08f',
							textDecoration: 'underline',
						},
						'& p': {
							margin: '4px 0',
						},
					}}
					dangerouslySetInnerHTML={{ __html: comment || t('noComment') }} 
				/>
			</Box>
		</Modal>
	);
};

interface OfferCardProps {
	offer: Offer;
	onClose?: () => void;
	onPay?: (offerId: string, requestId?: string) => void;
	onChat?: (offerId: string, requestId?: string) => void;
	onFieldbook?: (fieldbookUrl: string) => void;
	isMobile?: boolean;
	isCompleted?: boolean;
	isArchived?: boolean;
	chatLoading?: boolean;
	onRefresh?: () => void; // Add callback to refresh offers data
}

export const OfferCard: React.FC<OfferCardProps> = ({
	offer,
	onChat,
	isMobile,
	isCompleted = false,
	isArchived = false,
	chatLoading = false,
	onRefresh,
}) => {
	// Debug log to see the offer structure
	console.log('Offer data:', offer);
	
	const { t } = useTranslation();
	const { navigateWithLanguage } = useLanguageNavigate();
	const [paymentLoading, setPaymentLoading] = useState(false);
	const [isAccepted, setIsAccepted] = useState(false);
	const [commentModalOpen, setCommentModalOpen] = useState(false);
	const [markingAsRead, setMarkingAsRead] = useState(false);
	const [localCommentSeen, setLocalCommentSeen] = useState(false);

	// Destructure status from offer
	const { 
		id, 
		specialist, 
		price, 
		is_free, 
		status, 
		reference_id, 
		is_paid, 
		payment_status,
		active_for_days, 
		start_date, 
		end_date,
		original_date,
		offer_comment,
		comment_count = 0,
		farmer_seen_offer_comment = true,
	} = offer;

	// Debug logs for date fields and status
	console.log('Payment status:', payment_status);
	console.log('Status:', status);
	console.log('Is paid:', is_paid);
	console.log('Original date:', original_date);
	console.log('Start date:', start_date);
	console.log('End date:', end_date);
	
	// Determine which dates to display
	const displayStartDate = useMemo(() => {
		console.log('Calculating displayStartDate - payment_status:', payment_status, 'original_date:', original_date);
		
		let dateStr;
		
		// Check if original_date exists directly in the offer object
		if (payment_status === 'unpaid' && original_date?.start_date) {
			console.log('Using original start date:', original_date.start_date);
			dateStr = original_date.start_date;
		}
		// Check if original_date might be nested in a different property
		else if (payment_status === 'unpaid' && offer.original_date?.start_date) {
			console.log('Using nested original start date:', offer.original_date.start_date);
			dateStr = offer.original_date.start_date;
		}
		else {
			console.log('Using regular start date:', start_date);
			dateStr = start_date;
		}
		
		// Format the date as DD.MM.YYYY
		if (dateStr) {
			const date = new Date(dateStr);
			return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
		}
		
		return '';
	}, [payment_status, original_date, start_date, offer]);

	const displayEndDate = useMemo(() => {
		let dateStr;
		
		// Check if original_date exists directly in the offer object
		if (payment_status === 'unpaid' && original_date?.end_date) {
			dateStr = original_date.end_date;
		}
		// Check if original_date might be nested in a different property
		else if (payment_status === 'unpaid' && offer.original_date?.end_date) {
			dateStr = offer.original_date.end_date;
		}
		else {
			dateStr = end_date;
		}
		
		// Format the date as DD.MM.YYYY
		if (dateStr) {
			const date = new Date(dateStr);
			return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
		}
		
		return '';
	}, [payment_status, original_date, end_date, offer]);
	
	// Calculate time remaining until end_date
	const timeRemaining = useMemo(() => {
		// For "Chat closes in X days" (when paid), use displayEndDate
		if (is_paid) {
			// Convert displayEndDate back to a Date object
			const endDateParts = displayEndDate.split('.');
			if (endDateParts.length === 3) {
				const day = parseInt(endDateParts[0], 10);
				const month = parseInt(endDateParts[1], 10) - 1; // Month is 0-indexed in JS Date
				const year = parseInt(endDateParts[2], 10);
				
				const endDateObj = new Date(year, month, day, 23, 59, 59); // End of the day
				const currentDate = new Date();
				
				// Calculate difference in milliseconds
				const diffMs = endDateObj.getTime() - currentDate.getTime();
				
				// Convert to days (rounded down)
				const diffDays = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
				
				console.log('End date:', endDateObj, 'Current date:', currentDate, 'Diff days:', diffDays);
				
				// If less than 1 day remaining, show hours
				if (diffDays === 0) {
					const diffHours = Math.max(1, Math.floor(diffMs / (1000 * 60 * 60)));
					return { value: diffHours, unit: 'hours' };
				}
				
				return { value: diffDays, unit: 'days' };
			}
		}
		
		// For "Request active for X days" (when not paid), use active_for_days
		return { value: active_for_days || 0, unit: 'days' };
	}, [displayEndDate, end_date, active_for_days, is_paid]);

	const handlePayment = useCallback(async (offerId: string) => {
		// If already accepted, don't try to accept again
		if (isAccepted) {
			console.log('Offer already accepted, skipping payment call');
			return;
		}
		
		// Set a timeout to prevent infinite loading
		const loadingTimeout = setTimeout(() => {
			console.log('Payment timeout reached, resetting loading state');
			setPaymentLoading(false);
		}, 10000); // 10 seconds timeout
		
		setPaymentLoading(true);
		try {
			console.log(`Handling payment/acceptance for offer: ${offerId}, is_free: ${is_free}`);
			const response = await acceptOffer(offerId);
			
			// Clear the timeout since we got a response
			clearTimeout(loadingTimeout);
			
			console.log('Offer acceptance response:', response);
			
			if (response.success) {
				// If it's a paid offer with a redirect URL, navigate to payment page
				if (response.data && response.data.redirect_url) {
					console.log('Redirecting to payment page:', response.data.redirect_url);
					window.location.href = response.data.redirect_url;
				} else {
					// For free offers, just mark as accepted locally and refresh
					console.log('Free offer accepted successfully');
					setIsAccepted(true);
					
					// Refresh offers list if callback is provided
					if (onRefresh) {
						console.log('Refreshing both requests and offers lists');
						onRefresh();
					}
				}
			} else {
				console.error('Offer acceptance failed:', response.error);
				alert(t('offerAcceptanceFailed', 'Failed to accept offer. Please try again.'));
			}
		} catch (error) {
			// Clear the timeout in case of error
			clearTimeout(loadingTimeout);
			console.error('Error accepting offer:', error);
			alert(t('offerAcceptanceFailed', 'Failed to accept offer. Please try again.'));
		} finally {
			// Ensure loading state is reset
			setPaymentLoading(false);
		}
	}, [onRefresh, is_free, t, isAccepted, id]);

	// Reset loading state when component unmounts
	useEffect(() => {
		return () => {
			setPaymentLoading(false);
		};
	}, []);

	const handlePay = (e: React.MouseEvent) => {
		e.stopPropagation();
		handlePayment(id);
	};

	const handleChat = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (onChat) {
			onChat(id, reference_id);
		}
	};

	const handleCommentClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		
		// Start API call first if needed, but don't wait for it
		if (comment_count > 0 && !farmer_seen_offer_comment && !localCommentSeen && !markingAsRead) {
			setMarkingAsRead(true);
			
			// Update local state immediately for better UX
			setLocalCommentSeen(true);
			
			// Make API call in the background
			markCommentAsRead(id)
				.catch((error) => {
					console.error('Error marking comment as read:', error);
					// If API call fails, revert the local state
					setLocalCommentSeen(false);
				})
				.finally(() => {
					setMarkingAsRead(false);
				});
		}
		
		// Open the modal immediately after starting the API call
		// This happens synchronously, so the modal opens right away
		setTimeout(() => {
			setCommentModalOpen(true);
		}, 0);
	};

	const handleCloseModal = () => {
		setCommentModalOpen(false);
		
		// Only refresh data when explicitly requested (e.g., on page navigation or manual refresh)
		// We've already updated the local state, so no need to refresh immediately
	};

	const getStatusColorClasses = () => {
		if (isCompleted) {
			return 'bg-[#EAFBF7] border-[#00AB89]';
		} else if (is_paid) {
			return 'bg-[#EAFBF7] border-[#00AB89]';
		} else {
			return 'bg-[#FFF] border-[#E9ECEF]';
		}
	};

	const handleProfileClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		navigateWithLanguage(`/specialist/profile/${specialist.id}`);
	};

	return (
		<div className="bg-white rounded-lg shadow-md mb-4 overflow-hidden">
			<div className="p-3">
				{/* Header with specialist info and price */}
				<div className="flex justify-between items-center mb-2">
					{/* Specialist info */}
					<div className="flex items-center gap-3">
						<Avatar
							src={specialist.profile_image_url}
							alt={specialist.full_name}
							sx={{
								width: isMobile ? 40 : 48,
								height: isMobile ? 40 : 48,
								cursor: 'pointer',
							}}
							onClick={handleProfileClick}
						/>

						<div className="flex flex-col">
							<Typography
								onClick={handleProfileClick}
								sx={{
									cursor: 'pointer',
									'&:hover': {
										textDecoration: 'underline',
									},
									fontSize: isMobile ? '14px' : '16px'
								}}
								className="font-roboto font-bold text-[16px] text-dark leading-[140%]"
							>
								{specialist.full_name}
							</Typography>

							{/* Country with flag */}
							<div className="flex items-center mt-1 gap-2">
								<img
									src={`${flagCDN}${specialist.country?.alpha_two?.toLowerCase() || specialist.country?.id?.toLowerCase() || 'xx'}.png`}
									alt={specialist.country?.alpha_two || specialist.country?.id || ''}
									style={{ width: isMobile ? 14 : 16, borderRadius: '2px' }}
								/>
								<span
									className="text-dark font-roboto text-base font-normal"
									style={{ fontSize: isMobile ? '12px' : '16px' }}
								>
									{specialist.country?.name || t('unknown')}
								</span>
							</div>

							{/* Display dates */}
							<span
								className="text-[#7B809A] font-roboto text-sm font-normal mt-1"
								style={{ fontSize: isMobile ? '12px' : '14px', display: 'block' }}
							>
								{displayStartDate && displayEndDate ? 
									`${displayStartDate} - ${displayEndDate}` : 
									t('datesNotAvailable')}
							</span>
						</div>
					</div>

					{/* Price Display */}
					<div
						className={`rounded-[8px] border font-roboto text-[16px] font-[600] leading-normal flex items-center justify-center relative ${getStatusColorClasses()}`}
						style={{
							width: isMobile ? '48px' : '64px',
							height: isMobile ? '48px' : '64px',
							fontSize: isMobile ? '14px' : '16px',
						}}
					>
						{(is_paid || isCompleted) && !isArchived && (
							<CheckCircleIcon
								sx={{
									color: '#00AB89',
									fontSize: isMobile ? '18px' : '24px',
								}}
								className="absolute top-[-10px] right-[-10px] text-white fill-[#00AB89] rounded-[8px] p-[2px] bg-white"
							/>
						)}

						{is_free ? (
							<span className="text-dark font-roboto text-base font-bold leading-[143.75%]">
								{t('free')}
							</span>
						) : (
							<span className="text-dark font-roboto text-base font-bold leading-[143.75%]">
								€{price}
							</span>
						)}
					</div>
				</div>

				{/* Status Bar - Hidden for archived offers */}
				{!isArchived && (
					<div className="flex justify-between items-center mb-3 py-[5px] px-[10px] bg-disabled border-t border-b border-[#E9ECEF]">
						<p
							className={`font-roboto text-sm font-normal leading-[140%] ${isCompleted ? 'text-[#00AB89]' : 'text-[#7B809A]'}`}
							style={{ fontSize: isMobile ? '11px' : '14px' }}
						>
							{isCompleted
								? t('completed', 'Completed')
								: is_paid
									? timeRemaining.unit === 'hours'
										? `Chat closes in ${timeRemaining.value} hours`
										: `Chat closes in ${timeRemaining.value} days`
									: `Request active for ${timeRemaining.value} days`}
						</p>

						{!isMobile && (
							<div className="flex items-center gap-2">
								<Button
									startIcon={<BookIcon />}
									sx={{
										fontFamily: 'Roboto',
										fontSize: '16px',
										fontWeight: 700,
										padding: 0,
										textTransform: 'capitalize',
										textDecoration: 'underline',
									}}
									variant="text"
									disabled
								>
									{t('fieldbook')}
								</Button>
								
								{/* Comment Badge */}
								<Button
									sx={{
										fontFamily: 'Roboto',
										fontSize: '16px',
										fontWeight: 700,
										padding: '0 8px',
										textTransform: 'capitalize',
										textDecoration: 'underline',
										color: '#3da08f',
										position: 'relative',
									}}
									variant="text"
									onClick={handleCommentClick}
								>
									{t('comment')}
									{comment_count > 0 && (
										<Box
											sx={{
												position: 'absolute',
												top: '-8px',
												right: '-8px',
												width: '20px',
												height: '20px',
												borderRadius: '50%',
												backgroundColor: (!farmer_seen_offer_comment && !localCommentSeen) ? '#f44336' : '#3da08f',
												color: 'white',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												fontSize: '12px',
												fontWeight: 'bold',
												animation: (!farmer_seen_offer_comment && !localCommentSeen) ? `${pulseAnimation} 2s infinite` : 'none',
											}}
										>
											{comment_count}
										</Box>
									)}
								</Button>
							</div>
						)}
					</div>
				)}

				{/* Action Buttons - Modified for archived */}
				<div className="flex justify-between w-full px-3 pb-3">
					<div className="flex items-center space-x-2">
						<Button
							variant="text"
							onClick={handleChat}
							loading={chatLoading}
							disabled={isCompleted || isArchived}
							sx={{
								minWidth: isMobile ? '40px' : '68px',
								textDecoration: 'underline',
								fontSize: isMobile ? '12px' : '14px',
								opacity: isCompleted || isArchived ? 0.5 : 1,
								padding: '6px 4px',
							}}
							startIcon={<ChatIcon sx={{ fontSize: isMobile ? '16px' : '24px' }} />}
						>
							{t('chat')}
						</Button>

						{/* Comment Button for Mobile */}
						{isMobile && (
							<Button
								sx={{
									fontFamily: 'Roboto',
									fontSize: '14px',
									fontWeight: 700,
									padding: '6px 12px',
									textTransform: 'capitalize',
									color: '#3da08f',
									position: 'relative',
									textDecoration: 'underline',
								}}
								variant="text"
								onClick={handleCommentClick}
							>
								{t('comment')}
								{comment_count > 0 && (
									<Box
										sx={{
											position: 'absolute',
											top: '-5px',
											right: '-5px',
											width: '20px',
											height: '20px',
											borderRadius: '50%',
											backgroundColor: (!farmer_seen_offer_comment && !localCommentSeen) ? '#f44336' : '#3da08f',
											color: 'white',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											fontSize: '12px',
											fontWeight: 'bold',
											animation: (!farmer_seen_offer_comment && !localCommentSeen) ? `${pulseAnimation} 2s infinite` : 'none',
										}}
									>
										{comment_count}
									</Box>
								)}
							</Button>
						)}
					</div>

					{/* Pay/Accept Button */}
					{!is_paid && !isCompleted && !isArchived && (
						<Button
							variant="contained"
							onClick={handlePay}
							loading={paymentLoading}
							disabled={isAccepted || paymentLoading}
							sx={{
								backgroundColor: isAccepted ? '#00AB89' : '#3da08f',
								color: 'white',
								'&:hover': {
									backgroundColor: isAccepted ? '#00AB89' : '#2c7a6b',
								},
								fontSize: isMobile ? '12px' : '14px',
								padding: isMobile ? '6px 12px' : '8px 16px',
							}}
						>
							{isAccepted ? t('accepted') : (is_free ? t('accept') : t('pay'))}
						</Button>
					)}

					{/* Paid/Accepted Button - shown when status is Accepted and is_paid is true */}
					{status === 'Accepted' && is_paid === true && !isCompleted && !isArchived && (
						<Button
							variant="contained"
							disabled
							sx={{
								backgroundColor: '#00AB89',
								color: 'white',
								fontSize: isMobile ? '12px' : '14px',
								padding: isMobile ? '6px 12px' : '8px 16px',
							}}
							startIcon={<CheckCircleIcon />}
						>
							{is_free ? t('accepted') : t('paid')}
						</Button>
					)}
				</div>
			</div>
			
			{/* Comment Modal */}
			<CommentModal 
				open={commentModalOpen} 
				handleClose={handleCloseModal} 
				comment={offer_comment || ''} 
			/>
		</div>
	);
};
